import * as amplitude from '@amplitude/analytics-browser';

/**
 * Initialize Amplitude analytics
 * @param {string} apiKey - Amplitude API key (from environment variable)
 */
export const initAnalytics = (apiKey) => {
  if (!apiKey) {
    console.warn('Amplitude API key not found. Analytics will not be initialized.');
    return;
  }

  try {
    amplitude.init(apiKey, {
      defaultTracking: {
        pageViews: false, // We'll track page views manually for React Router
        sessions: true,
        formInteractions: false, // We'll track form submissions manually
        fileDownloads: true,
      },
    });
    console.log('Amplitude analytics initialized');
  } catch (error) {
    console.error('Failed to initialize Amplitude:', error);
  }
};

/**
 * Track a custom event
 * @param {string} eventName - Name of the event
 * @param {Object} eventProperties - Additional properties for the event
 */
export const trackEvent = (eventName, eventProperties = {}) => {
  try {
    amplitude.track(eventName, eventProperties);
  } catch (error) {
    console.error('Failed to track event:', error);
  }
};

/**
 * Track page view
 * @param {string} pageName - Name of the page
 * @param {Object} properties - Additional properties
 */
export const trackPageView = (pageName, properties = {}) => {
  try {
    amplitude.track('Page Viewed', {
      page_name: pageName,
      ...properties,
    });
  } catch (error) {
    console.error('Failed to track page view:', error);
  }
};

/**
 * Track form submission
 * @param {string} formName - Name of the form
 * @param {Object} formData - Form data (sanitized, no PII)
 */
export const trackFormSubmission = (formName, formData = {}) => {
  try {
    amplitude.track('Form Submitted', {
      form_name: formName,
      // Only include non-PII data
      has_name: !!formData.name,
      has_email: !!formData.email_address,
      selected_gig: formData.selected_gig || null,
    });
  } catch (error) {
    console.error('Failed to track form submission:', error);
  }
};

/**
 * Track form error
 * @param {string} formName - Name of the form
 * @param {string} errorType - Type of error
 */
export const trackFormError = (formName, errorType) => {
  try {
    amplitude.track('Form Error', {
      form_name: formName,
      error_type: errorType,
    });
  } catch (error) {
    console.error('Failed to track form error:', error);
  }
};

/**
 * Track button click
 * @param {string} buttonName - Name/identifier of the button
 * @param {Object} properties - Additional properties
 */
export const trackButtonClick = (buttonName, properties = {}) => {
  try {
    amplitude.track('Button Clicked', {
      button_name: buttonName,
      ...properties,
    });
  } catch (error) {
    console.error('Failed to track button click:', error);
  }
};

/**
 * Track gig selection
 * @param {string} gigName - Name of the selected gig
 */
export const trackGigSelection = (gigName) => {
  try {
    amplitude.track('Gig Selected', {
      gig_name: gigName,
    });
  } catch (error) {
    console.error('Failed to track gig selection:', error);
  }
};

/**
 * Set user properties
 * @param {Object} properties - User properties to set
 */
export const setUserProperties = (properties) => {
  try {
    amplitude.identify(
      amplitude.Identify().set(properties)
    );
  } catch (error) {
    console.error('Failed to set user properties:', error);
  }
};

/**
 * Set user ID (call this after user signs up or logs in)
 * @param {string} userId - User ID
 */
export const setUserId = (userId) => {
  try {
    amplitude.setUserId(userId);
  } catch (error) {
    console.error('Failed to set user ID:', error);
  }
};
